import React, { useEffect, useMemo, useRef, useState } from 'react'
import { WriteMessageForm } from 'components/inbox/WriteMessageForm'
import {
  createIssueInboxConversation,
  createReviewInboxConversation
} from 'components/inbox/services/createInboxConversation'
import { createInboxMessage } from 'components/inbox/services/createInboxMessage'
import { fetchInboxConversation } from 'components/inbox/services/fetchInboxConversation'
import { fetchInboxConversations } from 'components/inbox/services/fetchInboxConversations'
import { fetchIssueReviewerRecipient } from 'components/inbox/services/fetchInboxUsers'
import type { InboxConversationDetail, InboxMessage, ReviewerDisplayState } from 'components/inbox/inboxTypes'
import type { Review as ReviewType } from 'components/supplier_show/prop_shapes/review'
import { MessageModalLayout } from 'components/inbox/shared/MessageModalLayout'
import { ReviewConversationRecipientPills } from 'components/inbox/shared/ReviewConversationRecipientPills'
import { IssueLinkingNote } from 'components/inbox/message_modals/IssueLinkingNote'
import {
  appendMessageToConversation,
  buildReviewerRecipient,
  type IssueContext,
  type MessageSubjectReview,
  renderIssueConversationContent,
  renderMessageThread,
  useMessageSubjectReview
} from 'components/inbox/message_modals/messageModalSubjectHelpers'

interface ExistingConversationRef {
  id: number
}

const defaultFetchConversation = async (reviewId: number): Promise<ExistingConversationRef | null> => {
  const conversations = await fetchInboxConversations(reviewId)
  const firstConversation = conversations[0]
  return firstConversation == null ? null : { id: firstConversation.id }
}

interface Props {
  review: ReviewType
  reviewerDisplayState: ReviewerDisplayState
  onClose: () => void
  onConversationCreated?: (conversationId: number) => void
  fetchConversation?: (reviewId: number) => Promise<ExistingConversationRef | null>
  title?: string
  testId?: string
  issueContext?: IssueContext
}

type DirectMessageSubject =
  | {
    kind: 'review'
    review: MessageSubjectReview
    fetchExistingConversation: (reviewId: number) => Promise<ExistingConversationRef | null>
  }
  | {
    kind: 'issue'
    review: MessageSubjectReview
    issue: IssueContext
  }

interface DirectMessageSubjectConfig {
  recipientField?: React.ReactNode
  contentAfterTextarea?: React.ReactNode
  showShareWithSupplierCTA: boolean
  showReviewTile: (conversationDetail: InboxConversationDetail | null) => boolean
  renderConversationContent: (
    conversationDetail: InboxConversationDetail | null,
    existingMessages: InboxMessage[],
    onActiveIssueClick?: () => void
  ) => React.ReactNode
  createConversation: (messageBody: string) => Promise<InboxConversationDetail>
}

const fetchIssueReviewerParticipantId = async (issueId: number): Promise<number> => {
  const reviewer = await fetchIssueReviewerRecipient(issueId)

  if (reviewer == null) {
    throw new Error('Issue reviewer recipient is missing')
  }

  return reviewer.id
}

const buildDirectMessageSubject = (
  review: MessageSubjectReview,
  issueContext: IssueContext | undefined,
  fetchExistingConversation: (reviewId: number) => Promise<ExistingConversationRef | null>
): DirectMessageSubject => (
  issueContext == null
    ? { kind: 'review', review, fetchExistingConversation }
    : { kind: 'issue', review, issue: issueContext }
)

const buildDirectMessageConfig = (subject: DirectMessageSubject): DirectMessageSubjectConfig => {
  switch (subject.kind) {
    case 'review':
      return {
        showShareWithSupplierCTA: !subject.review.shareWithSupplier,
        showReviewTile: () => true,
        renderConversationContent: (_conversationDetail, existingMessages) => renderMessageThread(existingMessages),
        createConversation: async (messageBody) => await createReviewInboxConversation(subject.review.id, messageBody)
      }
    case 'issue': {
      const reviewerRecipient = buildReviewerRecipient(subject.review)

      return {
        recipientField: (
          <div className='message-reviewer-modal__recipient-field'>
            <div className='message-reviewer-modal__recipient-label'>To:</div>
            <div className='message-reviewer-modal__recipient-list'>
              <ReviewConversationRecipientPills participants={reviewerRecipient} />
            </div>
          </div>
        ),
        contentAfterTextarea: <IssueLinkingNote issueName={subject.issue.issueName} />,
        showShareWithSupplierCTA: !subject.review.shareWithSupplier,
        showReviewTile: (conversationDetail) => conversationDetail == null,
        renderConversationContent: renderIssueConversationContent,
        createConversation: async (messageBody) => {
          const reviewerParticipantId = await fetchIssueReviewerParticipantId(subject.issue.issueId)
          return await createIssueInboxConversation(
            subject.issue.issueId,
            messageBody,
            [reviewerParticipantId]
          )
        }
      }
    }
  }
}

export const DirectMessageModal: React.FC<Props> = ({
  review,
  reviewerDisplayState,
  fetchConversation,
  onClose,
  onConversationCreated,
  title = 'Direct Message',
  testId = 'direct-message-modal',
  issueContext
}) => {
  const fetchExistingConversation =
    fetchConversation ?? defaultFetchConversation
  const subjectReview = useMessageSubjectReview(review)
  const subjectIssue = useMemo<IssueContext | undefined>(() => (
    issueContext == null
      ? undefined
      : {
          issueId: issueContext.issueId,
          issueName: issueContext.issueName
        }
  ), [issueContext?.issueId, issueContext?.issueName])
  const subject = useMemo(
    () => buildDirectMessageSubject(subjectReview, subjectIssue, fetchExistingConversation),
    [fetchExistingConversation, subjectIssue, subjectReview]
  )
  const subjectConfig = useMemo(() => buildDirectMessageConfig(subject), [subject])
  const hasIssueContext = subjectIssue != null
  const loadExistingConversation = useMemo<(() => Promise<ExistingConversationRef | null>) | null>(() => (
    hasIssueContext
      ? null
      : async () => await fetchExistingConversation(subjectReview.id)
  ), [fetchExistingConversation, hasIssueContext, subjectReview.id])
  const [conversationExists, setConversationExists] = useState<boolean>(false)
  const [conversationId, setConversationId] = useState<number | null>(null)
  const [conversationDetail, setConversationDetail] = useState<InboxConversationDetail | null>(null)
  const [existingMessages, setExistingMessages] = useState<InboxMessage[]>([])
  const [isLoadingConversation, setIsLoadingConversation] = useState<boolean>(true)
  const [conversationLoadError, setConversationLoadError] = useState<string | null>(null)
  const [sendError, setSendError] = useState<string | null>(null)
  const scrollRegionRef = useRef<HTMLDivElement>(null)
  const contentAfterTextarea = conversationExists ? null : subjectConfig.contentAfterTextarea

  useEffect(() => {
    let isMounted = true
    setIsLoadingConversation(true)
    setConversationLoadError(null)

    if (loadExistingConversation == null) {
      setConversationId(null)
      setConversationExists(false)
      setConversationDetail(null)
      setExistingMessages([])
      setIsLoadingConversation(false)
      return () => {
        isMounted = false
      }
    }

    void loadExistingConversation().then(async (conversation) => {
      if (!isMounted) return
      setConversationId(conversation?.id ?? null)
      setConversationExists(conversation != null)

      if (conversation == null) {
        setExistingMessages([])
        return
      }

      try {
        const detail = await fetchInboxConversation(conversation.id)
        if (!isMounted) return
        setConversationDetail(detail)
        setExistingMessages(detail.messages)
        setConversationLoadError(null)
      } catch {
        if (!isMounted) return
        // Preserve existing conversation id so new sends append to it
        setExistingMessages([])
        setConversationLoadError('Unable to load previous messages. You can still send a new message.')
      }
    }).catch(() => {
      if (!isMounted) return
      setConversationId(null)
      setConversationExists(false)
      setExistingMessages([])
      setConversationLoadError('Unable to load previous messages. You can still send a new message.')
    }).finally(() => {
      if (!isMounted) return
      setIsLoadingConversation(false)
    })

    return () => {
      isMounted = false
    }
  }, [loadExistingConversation])

  useEffect(() => {
    if (scrollRegionRef.current == null || conversationId == null) return
    scrollRegionRef.current.scrollTop = scrollRegionRef.current.scrollHeight
  }, [conversationId, existingMessages.length])

  const handleCreateOrSendMessage = async (messageBody: string): Promise<'sent' | undefined> => {
    setSendError(null)
    if (isLoadingConversation) return

    try {
      if (conversationId == null) {
        const conversation = await subjectConfig.createConversation(messageBody)
        if (onConversationCreated != null) onConversationCreated(conversation.id)
        setConversationId(conversation.id)
        setConversationExists(true)
        setConversationDetail(conversation)
        // Render the first sent message immediately after creating the conversation
        setExistingMessages(conversation.messages)
        return 'sent'
      }

      const message = await createInboxMessage(conversationId, messageBody)
      // Append the new message locally so the thread stays in sync without a refetch
      setExistingMessages((prev) => [...prev, message])
      setConversationDetail((previousConversation) => appendMessageToConversation(previousConversation, message))
      return 'sent'
    } catch {
      setSendError('Unable to send message. Please try again.')
    }
  }

  return (
    <MessageModalLayout
      review={review}
      title={title}
      onClose={onClose}
      testId={testId}
      showReviewTile={subjectConfig.showReviewTile(conversationDetail)}
      issueTrackingCTAMode={hasIssueContext ? 'activeOnly' : undefined}
      onActiveIssueClick={hasIssueContext ? onClose : undefined}
      supplierLinkMode={hasIssueContext ? 'canary' : undefined}
      recipientField={subjectConfig.recipientField}
      scrollRegionRef={scrollRegionRef}
      scrollableContent={
        <>
          {isLoadingConversation && (
            <div className='message-reviewer-modal__existing-conversation'>
              Loading conversation…
            </div>
          )}

          {!isLoadingConversation && conversationExists && (
            <div className='message-reviewer-modal__existing-conversation inbox-message-container'>
              <div className='inbox-message-scroll'>
                {subjectConfig.renderConversationContent(conversationDetail, existingMessages, hasIssueContext ? onClose : undefined)}
              </div>
            </div>
          )}
        </>
      }
      footerContent={
        <>
          {conversationLoadError != null && (
            <div className='write-message-error' role='alert'>
              {conversationLoadError}
            </div>
          )}

          {sendError != null && (
            <div className='write-message-error' role='alert'>
              {sendError}
            </div>
          )}

          <WriteMessageForm
            reviewerDisplayState={reviewerDisplayState}
            disableSend={isLoadingConversation}
            contentAfterTextarea={contentAfterTextarea}
            onSend={handleCreateOrSendMessage}
            onCancel={onClose}
            showShareWithSupplierCTA={subjectConfig.showShareWithSupplierCTA}
            reviewAuthorName={review.author.name}
          />
        </>
      }
    />
  )
}
